import { useEffect, useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getReferencesOptions } from '../api';
import type { LevelValue, References } from '../types';
import type { Option } from '../../../types/shared';
import { useCurrentNetwork } from '../../../hooks';
import useRulePageMode from './useRulePageMode';
import useCurrentTasksFormStore from './useCurrentTasksFormStore';
import { useFormContext } from 'react-hook-form';
import type { ConditionVariantProps } from '../types';
import { COUNT_METRICS } from '../constants';
import type { BasicFormValues } from '../facebook';

const useMetricLevelConstraints = ({ indexCondition, indexTask, field, indexVariant }: ConditionVariantProps) => {
  const { isRead } = useRulePageMode();
  const network = useCurrentNetwork();
  const isVariant = typeof indexVariant === 'number';
  const tasksFormStore = useCurrentTasksFormStore();
  const condition = tasksFormStore.getConditionMetric(indexTask, indexCondition);

  const metric = field.includes('second') ? condition.second_metric : condition.metric;
  const metricLevel = field.includes('second') ? condition.second_metric_level : condition.metric_level;
  const isCountMetric = COUNT_METRICS.includes(String(metric));

  const { getValues } = useFormContext<BasicFormValues>();
  const ruleLevel = getValues('level') as LevelValue;

  const { data: levels } = useSuspenseQuery({
    ...getReferencesOptions(network),
    select: (data: References): Option[] => data.conditions.levels.map(({ name, id }) => ({ value: id, label: name })),
  });

  const options = useMemo(() => {
    if (network === 'facebook') {
      // if isCountMetric filter options to have only option that equals to the rule level
      if (isCountMetric) {
        const level = levels.find((lvl) => lvl.value === ruleLevel);
        return level ? [level] : [];
      }
      return levels;
    }

    if (network === 'google') {
      return levels;
    }

    return [];
  }, [isCountMetric, levels, network, ruleLevel]);

  useEffect(() => {
    if (network === 'facebook') {
      // set metric level to the current rule level when metric is isCountMetric
      if (isCountMetric && metricLevel !== ruleLevel) {
        tasksFormStore.setConditionMetric(indexTask, indexCondition, field, ruleLevel);
      }
    }
  }, [field, indexCondition, indexTask, isCountMetric, metricLevel, network, ruleLevel, tasksFormStore]);

  // disable level in condition for facebook when metric is one of COUNT_METRICS
  return { disabled: isRead || isCountMetric || isVariant, options, isVariant };
};

export default useMetricLevelConstraints;
